import { modals } from '@mantine/modals';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { useTranslation } from 'locales/logic';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import posthog from 'posthog-js';
import type { ReactNode } from 'react';
import { captureEvent } from 'shared/analytics/capture-event';
import type { ActionButtonDefinition } from '../action-button-definition';
import type { ReminderDocumentType } from '../api/preview-reminder-request';
import type { SendReminderVariables } from '../api/send-reminder-variables';
import { ReminderWizard } from '../ReminderWizard/ReminderWizard';
import { computeCommonProps } from './common';

type SendReminder = (
  variables: SendReminderVariables,
  options: { onSuccess?: () => void; onSettled?: () => void },
) => void;

type ReminderWizardActionConfig = {
  documentType: ReminderDocumentType;
  translationKey: string;
  icon: ReactNode;
  templateId: string;
  send: SendReminder;
};

export function useReminderWizardAction({
  documentType,
  translationKey,
  icon,
  templateId,
  send,
}: ReminderWizardActionConfig): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const { t } = useTranslation();
  const debtor = useDebtor();

  return (action, invoices) => {
    const handler = () =>
      modals.open({
        size: '90%',
        title: t(translationKey),
        children: (
          <ReminderWizard
            debtorId={Number(debtor.id)}
            invoices={invoices}
            templateId={templateId}
            documentType={documentType}
            onSubmit={variables => {
              captureEvent(posthog, 'collection:invoices_treated', {
                action_type: documentType,
                invoices_treated_count: [variables.invoiceId, ...variables.additionalInvoiceIds]
                  .length,
              });
              return new Promise<void>(resolve => {
                send(variables, {
                  onSuccess: () => modals.closeAll(),
                  onSettled: () => resolve(),
                });
              });
            }}
            onCancel={() => modals.closeAll()}
          />
        ),
      });

    return {
      icon,
      onClick: handler,
      translationKey,
      ...computeCommonProps(action, invoices),
    };
  };
}
